import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";
import { Activity, Clock, CheckCircle2, AlertOctagon, Target, TrendingUp } from "lucide-react";

const COLORS = ["#0369A1", "#10B981", "#F59E0B", "#EF4444", "#334155"];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <div className="p-10 text-red-600">{err}</div>;
  if (!stats) return <div className="p-10 text-slate-500">Loading dashboard…</div>;

  const greeting = `Welcome back, ${user?.name?.split(" ")[0] || "there"}.`;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle={greeting}
        testId="dashboard-header"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        <StatCard testId="kpi-coded-today" label="Charts Coded Today" value={stats.coded_today} icon={Activity} />
        <StatCard testId="kpi-qa-pending" label="QA Pending" value={stats.qa_pending} icon={Clock} accent="text-amber-600" />
        <StatCard testId="kpi-qa-completed" label="QA Completed" value={stats.qa_completed} icon={CheckCircle2} accent="text-emerald-600" />
        <StatCard testId="kpi-coding-accuracy" label="Coding %" value={`${stats.coding_accuracy ?? 0}%`} icon={Target} accent="text-fuchsia-700" />
        <StatCard testId="kpi-oasis-accuracy" label="OASIS %" value={`${stats.oasis_accuracy ?? 0}%`} icon={Target} accent="text-indigo-700" />
        <StatCard testId="kpi-accuracy" label="Overall %" value={`${stats.accuracy}%`} icon={Target} accent="text-sky-700" />
        <StatCard testId="kpi-tat" label="Avg TAT" value={`${stats.tat_hours}h`} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Productivity */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-2" data-testid="productivity-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-medium text-slate-900">Productivity — last 7 days</h3>
              <p className="text-xs text-slate-500">Charts submitted per day</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.productivity_series}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0369A1" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error trends */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="error-trends-chart">
          <div className="flex items-center gap-2 mb-4">
            <AlertOctagon size={16} className="text-red-500" />
            <h3 className="font-heading text-lg font-medium text-slate-900">Error trends</h3>
          </div>
          {stats.error_trends.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">No errors recorded yet.</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.error_trends}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="category" tick={{ fontSize: 12, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
